import type { AnimeatableIconRef } from '@parkdna/ui/icons/types';
import type { LucideIcon } from 'lucide-react';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import React from 'react';

import SidebarExternalLinkItem from './SidebarExternalLinkItem';
import SidebarMenuItem from './SidebarMenuItem';
import SidebarSubMenu from './SidebarSubMenu';

type AnimatedIconComponent = ForwardRefExoticComponent<
  { size?: number } & RefAttributes<AnimeatableIconRef>
>;

type IconComponent = AnimatedIconComponent | LucideIcon;

export type MenuItem = (
  | {
      title: string;
      to: string;
      iconComponent: IconComponent;
      type: 'menu-item';
      /**
       * If true, only match when the location exactly matches the `to` path.
       * Use for child routes that shouldn't match when on sibling routes.
       * Default: false (allows parent to stay highlighted on child routes)
       */
      end?: boolean;
    }
  | {
      title: string;
      href: string;
      iconComponent: IconComponent;
      type: 'external-link';
      hint?: {
        message: React.ReactNode;
        storageKey: string;
      };
    }
  | {
      type: 'other';
      element: React.ReactElement;
    }
  | {
      type: 'sub-menu';
      items: MenuItem[];
      iconComponent: IconComponent;
      title: string;
    }
) & {
  dataCy?: string;
  dataTestId?: string;
  isHidden?: boolean;
  align?: 'bottom';
};

interface SidebarMenuItemsProps {
  items: MenuItem[];
}

export const SidebarMenuItems: React.FC<SidebarMenuItemsProps> = ({
  items,
}) => {
  return items.map((menuItem, _index) => {
    if (menuItem.isHidden) {
      return null;
    }

    if (menuItem.type === 'other') {
      return (
        <React.Fragment
          key={`other-${menuItem.element.key || `element-${Date.now()}-${Math.random()}`}`}
        >
          {menuItem.element}
        </React.Fragment>
      );
    }

    if (menuItem.type === 'external-link') {
      return (
        <SidebarExternalLinkItem
          key={menuItem.title}
          title={menuItem.title}
          href={menuItem.href}
          iconComponent={menuItem.iconComponent}
          dataCy={menuItem.dataCy}
          dataTestId={menuItem.dataTestId}
          hint={menuItem.hint}
        />
      );
    }

    if (menuItem.type === 'sub-menu') {
      return (
        <SidebarSubMenu
          key={`submenu-${menuItem.title}`}
          label={menuItem.title}
          iconComponent={menuItem.iconComponent}
          dataCy={menuItem.dataCy}
        >
          <SidebarMenuItems items={menuItem.items} />
        </SidebarSubMenu>
      );
    }

    return (
      <SidebarMenuItem
        key={menuItem.title}
        title={menuItem.title}
        to={menuItem.to}
        iconComponent={menuItem.iconComponent}
        dataCy={menuItem.dataCy}
        dataTestId={menuItem.dataTestId}
        end={menuItem.end}
      />
    );
  });
};
